import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";
import { Button } from "../../../Ui_Components/Button";
import {
  downloadIngredientBulkUploadTemplate,
  UploadIngredientBulkResponse,
} from "../../../../services/ingredients";
import BulkUploadResultView from "./BulkUploadResultView";

export type BulkUploadMode = "import" | "modify";

interface BulkUploadModalProps {
  open: boolean;
  onClose: () => void;
  /* Called when the user confirms Import — the parent decides what to do with
     the file next (open preview, kick off upload, etc). */
  onImport: (file: File) => void;
  onDownloadTemplate?: () => void;
  /* Controls the copy: "import" renders the Bulk upload flow with an Import
     button; "modify" renders the Bulk modify flow with a Validate button. */
  mode?: BulkUploadMode;
  /* Noun used in the modal title, e.g. "ingredients" or "dishes". Kept
     configurable so the same picker can back both flows. */
  label?: string;
  /* When set, the picker UI is replaced with a formatted result view for the
     backend response. Parent keeps the modal open across the API round-trip
     so the user sees added/updated counts, error rows or header mismatches
     right where they uploaded the file. */
  uploadResult?: UploadIngredientBulkResponse | null;
  /* True while the upload API call is in flight. Disables Cancel + keeps the
     progress indicator visible so the modal is never in an inert state. */
  uploadInFlight?: boolean;
  /* Wired by the parent to re-post the picked sheet with
     ?download_errors=true and stream the annotated Excel back to the
     browser. Passed through to BulkUploadResultView which shows an
     inline "Download error file" button on any failure state. */
  onDownloadErrors?: () => Promise<void>;
}

// Accepts .xlsx / .xls only. Kept permissive on the MIME type since browsers
// disagree (application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
// vs application/vnd.ms-excel), so the extension check below is the source
// of truth.
const EXCEL_ACCEPT =
  ".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel";
const EXCEL_EXTENSIONS = [".xlsx", ".xls"];

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  open,
  onClose,
  onImport,
  onDownloadTemplate,
  mode = "import",
  label = "ingredients",
  uploadResult = null,
  uploadInFlight = false,
  onDownloadErrors,
}) => {
  const isModify = mode === "modify";
  const showingResult = uploadResult !== null;
  const title = showingResult
    ? isModify
      ? "Bulk modify result"
      : "Bulk upload result"
    : isModify
      ? `Bulk modify ${label}`
      : `Bulk upload ${label}`;
  const primaryLabel = isModify ? "Validate" : "Import";
  const inputRef = useRef<HTMLInputElement>(null);
  const readerRef = useRef<FileReader | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [downloading, setDownloading] = useState(false);

  // Reset every time the dialog is (re)opened so a previously-picked file
  // doesn't linger in the picker on the next visit.
  useEffect(() => {
    if (open) {
      setFile(null);
      setError(null);
      setUploading(false);
      setProgress(0);
      setDownloading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [open]);

  // Fetch the correct Excel template for the active mode and trigger a
  // browser download. If the parent supplied a custom handler it wins,
  // so callers can override for testing without changing this component.
  const handleDownloadTemplate = async () => {
    if (onDownloadTemplate) {
      onDownloadTemplate();
      return;
    }
    if (downloading) return;
    setDownloading(true);
    setError(null);
    try {
      await downloadIngredientBulkUploadTemplate(
        isModify ? "rename" : "normal",
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to download template. Please try again.",
      );
    } finally {
      setDownloading(false);
    }
  };

  if (!open) return null;

  const isExcel = (f: File) =>
    EXCEL_EXTENSIONS.some((ext) => f.name.toLowerCase().endsWith(ext));

  const startUpload = (f: File) => {
    setUploading(true);
    setProgress(0);
    const startedAt = Date.now();
    const reader = new FileReader();
    readerRef.current = reader;
    reader.onprogress = (ev) => {
      if (ev.lengthComputable) {
        // Cap at 95 so the bar doesn't jump to 100 before onload fires.
        const pct = Math.round((ev.loaded / ev.total) * 95);
        setProgress(pct);
      }
    };
    reader.onload = () => {
      setProgress(100);
      // Hold the filled bar briefly so tiny files still register visually
      // before we hand off to the preview modal.
      const remaining = Math.max(0, 400 - (Date.now() - startedAt));
      window.setTimeout(() => {
        readerRef.current = null;
        setUploading(false);
        onImport(f);
      }, remaining);
    };
    reader.onerror = () => {
      readerRef.current = null;
      setUploading(false);
      setProgress(0);
      setFile(null);
      setError("Failed to read the file. Please try again.");
    };
    reader.readAsArrayBuffer(f);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    if (!isExcel(picked)) {
      setError("Please choose an Excel file (.xlsx or .xls).");
      setFile(null);
      e.target.value = "";
      return;
    }
    setError(null);
    setFile(picked);
    startUpload(picked);
  };

  const handleClose = () => {
    if (readerRef.current) {
      readerRef.current.abort();
      readerRef.current = null;
    }
    setFile(null);
    setError(null);
    setUploading(false);
    setProgress(0);
    if (inputRef.current) inputRef.current.value = "";
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-md shadow-xl w-full max-w-[650px] mx-4">
        {/* Header */}
        <div className="px-6 pt-5 pb-4">
          <h2 className="text-lg sm:text-xl 3xl:text-[22px] font-semibold font-inter text-[#1F1F1F]">
            {title}
          </h2>
        </div>

        {/* Body — swaps to a formatted result panel once the backend
            response is in, so the user reads the outcome in the same
            popup instead of chasing a toast. */}
        <div className="px-6 pb-5 max-h-[70vh] overflow-y-auto">
          {showingResult ? (
            <BulkUploadResultView
              result={uploadResult!}
              onDownloadErrors={onDownloadErrors}
            />
          ) : (
            <>
              <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                Upload file
              </label>

              <div className="mt-2 flex items-stretch border border-[#E0E0E0] rounded-md overflow-hidden">
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  disabled={uploading || uploadInFlight}
                  className="bg-[#1F1F1F] text-white text-sm 3xl:text-base font-inter px-4 py-2 whitespace-nowrap hover:bg-[#333333] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Choose File
                </button>
                <span className="flex-1 px-3 py-2 text-sm 3xl:text-base font-inter text-[#6B7280] truncate">
                  {file ? file.name : "No file chosen"}
                </span>
              </div>

              <input
                ref={inputRef}
                type="file"
                accept={EXCEL_ACCEPT}
                className="hidden"
                onChange={handleFileChange}
              />

              {uploading && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs 3xl:text-sm font-inter text-[#1F1F1F]">
                      Uploading...
                    </span>
                    <span className="text-xs 3xl:text-sm font-inter text-[#6B7280]">
                      {progress}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-[#E8E8E8] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#0DB5B5] transition-all duration-200 ease-out"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              {uploadInFlight && !uploading && (
                <div className="mt-3 flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-[#0DB5B5] border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs 3xl:text-sm font-inter text-[#1F1F1F]">
                    Sending to server...
                  </span>
                </div>
              )}

              {error && (
                <p className="mt-2 text-xs 3xl:text-sm text-red-500 font-inter">
                  {error}
                </p>
              )}

              {/* Template hint */}
              <div className="mt-3 flex items-start gap-2 rounded-md bg-[#F9F9F9] px-3 py-2">
                <Info className="w-4 h-4 text-[#646464] mt-0.5 flex-shrink-0" />
                <p className="text-xs 3xl:text-sm font-inter text-[#646464]">
                  Need the format?{" "}
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    disabled={downloading}
                    className="text-[#646464] underline font-bold hover:text-[#0CA0A0] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {downloading ? "Downloading..." : "Download excel template"}
                  </button>
                  , mandatory columns are marked.{" "}
                  <span className="font-semibold">
                    Don't rename, move, add, or delete columns, or change the
                    formatting.
                  </span>
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer — collapses to a single Close button once we're showing
            the result view since there's no import action left to take. */}
        <div className="border-t border-[#E8E8E8] px-6 py-4 flex justify-end gap-3">
          {showingResult ? (
            <Button variant="primary" size="md" onClick={handleClose}>
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                size="md"
                onClick={handleClose}
                disabled={uploadInFlight}
              >
                Cancel
              </Button>
              {/* Import is auto-triggered once the file finishes uploading, so
                  this button is intentionally always disabled. */}
              <Button variant="primary" size="md" disabled>
                {primaryLabel}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BulkUploadModal;
